<script setup lang="ts">
import { ref, useSlots, watch, onUnmounted, type PropType } from 'vue';
import { exposedPromise, type ExposedPromise } from '@/services/utility';
import { modalRegister, modalRemove, modalSetOpened, modalOnTop, type ModalItem } from '@/services/modals';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  modalID: {
    type: String as PropType<ModalItem['modalID']>,
    default: () => modalRegister(),
  },
  // eslint-disable-next-line vue/require-prop-types
  defaultCloseValue: {},
  dialogStyle: {
    type: String,
    default: '',
  },
  dialogClass: {
    type: String,
    default: '',
  },
  forceKeepShowing: {
    type: Boolean,
    default: false,
  },
});

defineOptions({
  inheritAttrs: false,
});

const slots = useSlots();
const openPromise = ref<ExposedPromise<any> | null>(null);
const showing = defineModel<boolean>('open', { required: false, default: false });

defineExpose({
  open: function <Type>(): Promise<Type | undefined> {
    resolveOpenPromise();

    openPromise.value = exposedPromise<Type>();
    showing.value = true;

    return openPromise.value.promise;
  },

  close: function (value: any = props.defaultCloseValue) {
    closeModal(value);
  },
});

watch(
  showing,
  function (newShowing, oldShowing) {
    if (!newShowing && oldShowing) {
      resolveOpenPromise();
    }

    modalSetOpened(props.modalID, newShowing);
  },
  { immediate: true },
);

function resolveOpenPromise(value: any = props.defaultCloseValue) {
  if (openPromise.value) {
    openPromise.value.resolve(value);
    openPromise.value = null;
  }
}

function closeModal(value: any = props.defaultCloseValue) {
  if (props.forceKeepShowing) {
    return;
  }

  resolveOpenPromise(value);
  showing.value = false;
}

const modalParentElem = ref(null as null | HTMLElement);
function backdropCloseModal(e: MouseEvent) {
  if (
    !props.forceKeepShowing &&
    e.button === 0 &&
    modalParentElem.value &&
    e.currentTarget === modalParentElem.value &&
    e.target === modalParentElem.value
  ) {
    // Don't close the modal if they clicked on the scrollbar:
    if (e.offsetX > modalParentElem.value.clientWidth) {
      return;
    }

    resolveOpenPromise(props.defaultCloseValue);
    showing.value = false;
  }
}

onUnmounted(() => {
  resolveOpenPromise();
  modalRemove(props.modalID);
});
</script>

<template>
  <teleport to="body" v-if="showing">
    <div
      ref="modalParentElem"
      class="modal"
      tabindex="-1"
      v-bind="$attrs"
      @mousedown="backdropCloseModal($event)"
      :class="modalOnTop === modalID ? 'd-block show' : 'd-none'"
    >
      <div class="modal-dialog" @click.stop :class="dialogClass" :style="dialogStyle">
        <div class="modal-content">
          <div class="modal-header" v-if="slots.header">
            <slot name="header" :close="closeModal" />
          </div>
          <div class="modal-header" v-else-if="title">
            <h4>{{ title }}</h4>
            <button type="button" class="btn-close" @click="closeModal()" />
          </div>
          <div class="modal-body" v-if="slots.default">
            <slot :close="closeModal" />
          </div>
          <div class="modal-footer" v-if="slots.footer">
            <slot name="footer" :close="closeModal" />
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop" :class="modalOnTop === modalID ? 'show' : 'd-none'" />
  </teleport>
</template>
